import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";
import { CartProvider } from "@/context/CartContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { DesktopShell } from "@/components/DesktopShell";
import { RealtimeListener } from "@/components/RealtimeListener";

import Landing from "@/pages/Landing";
import Login from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Signup from "@/pages/Signup";
import Discover from "@/pages/Discover";
import Stores from "@/pages/Stores";
import ShowDetail from "@/pages/ShowDetail";
import Invite from "@/pages/Invite";
import Purchases from "@/pages/Purchases";
import Cart from "@/pages/Cart";
import Following from "@/pages/Following";
import Replays from "@/pages/Replays";
import MyShows from "@/pages/MyShows";
import CreateShow from "@/pages/CreateShow";
import Profile from "@/pages/Profile";
import Settings from "@/pages/Settings";
import Addresses from "@/pages/Addresses";
import About from "@/pages/About";
import Contact from "@/pages/Contact";
import Pitch from "@/pages/Pitch";
import Privacy from "@/pages/Privacy";
import HelpCenter from "@/pages/HelpCenter";
import Terms from "@/pages/Terms";
import SellerPolicy from "@/pages/SellerPolicy";
import DeleteAccount from "@/pages/DeleteAccount";
import Support from "@/pages/Support";
import SellerOrders from "@/pages/SellerOrders";
import StoreProfile from "@/pages/StoreProfile";
import PublicStore from "@/pages/PublicStore";
import Simulcast from "@/pages/Simulcast";
import Returns from "@/pages/Returns";
import Inventory from "@/pages/Inventory";
import Shipments from "@/pages/Shipments";
import ShippingSettings from "@/pages/ShippingSettings";
import Track from "@/pages/Track";
import AdminShipping from "@/pages/AdminShipping";
import Finances from "@/pages/Finances";
import Insights from "@/pages/Insights";
import Admin from "@/pages/Admin";
import AdminWaitlist from "@/pages/AdminWaitlist";
import Messages from "@/pages/Messages";
import Notifications from "@/pages/Notifications";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import Saved from "@/pages/Saved";
import Offers from "@/pages/Offers";
import {
  Download,
} from "@/pages/Placeholders";

function PublicOnly({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="center-load"><div className="spinner" /></div>;
  if (user) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <BrowserRouter>
          <CartProvider>
          <Toaster position="top-center" richColors />
          <RealtimeListener />
          <Routes>
            {/* Public */}
            <Route path="/landing" element={<PublicOnly><Landing /></PublicOnly>} />
            <Route path="/login" element={<PublicOnly><Login /></PublicOnly>} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<PublicOnly><Signup /></PublicOnly>} />
            <Route path="/about" element={<About />} />
            <Route path="/help" element={<HelpCenter />} />
            <Route path="/faq" element={<HelpCenter />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/pitch" element={<Pitch />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/terms" element={<Terms />} />
            <Route path="/seller-policy" element={<SellerPolicy />} />
            <Route path="/delete-account" element={<DeleteAccount />} />

            {/* App shell (protected) */}
            <Route element={<ProtectedRoute><DesktopShell /></ProtectedRoute>}>
              <Route path="/" element={<Discover />} />
              <Route path="/stores" element={<Stores />} />
              <Route path="/show/:id" element={<ShowDetail />} />
              <Route path="/sell" element={<CreateShow />} />
              <Route path="/messages" element={<Messages />} />
              <Route path="/messages/:otherId" element={<Messages />} />
              <Route path="/notifications" element={<Notifications />} />
              <Route path="/payment/success" element={<PaymentSuccess />} />
              <Route path="/payment/cancel" element={<PaymentCancel />} />
              <Route path="/invite" element={<Invite />} />
              <Route path="/refer" element={<Invite />} />
              <Route path="/purchases" element={<Purchases />} />
              <Route path="/cart" element={<Cart />} />
              <Route path="/following" element={<Following />} />
              <Route path="/saved" element={<Saved />} />
              <Route path="/offers" element={<Offers />} />
              <Route path="/replays" element={<Replays />} />
              <Route path="/my-shows" element={<MyShows />} />
              <Route path="/store-profile" element={<StoreProfile />} />
              <Route path="/store/:sellerId" element={<PublicStore />} />
              <Route path="/seller-orders" element={<SellerOrders />} />
              <Route path="/returns" element={<Returns />} />
              <Route path="/inventory" element={<Inventory />} />
              <Route path="/shipments" element={<Shipments />} />
              <Route path="/shipping-settings" element={<ShippingSettings />} />
              <Route path="/simulcast" element={<Simulcast />} />
              <Route path="/track/:trackingNumber" element={<Track />} />
              <Route path="/admin-shipping" element={<AdminShipping />} />
              <Route path="/finances" element={<Finances />} />
              <Route path="/insights" element={<Insights />} />
              <Route path="/download" element={<Download />} />
              <Route path="/profile" element={<Profile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/addresses" element={<Addresses />} />
              <Route path="/support" element={<Support />} />
              <Route path="/admin" element={<Admin />} />
              <Route path="/admin-waitlist" element={<AdminWaitlist />} />
            </Route>

            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
          </CartProvider>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
